import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useGetServiceAlbumListQuery, useRenameServiceAlbumMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, yupString} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';

interface FormValues {
  description: string;
}

interface VehicleRenameAlbumDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  serviceAlbumId: string;
  onComplete?: () => void;
}

export function VehicleRenameAlbumDialog(props: VehicleRenameAlbumDialogProps) {
  const {
    data: serviceAlbums,
    isLoading,
    isError,
  } = useGetServiceAlbumListQuery({vehicleId: props.vehicleId});

  const [renameServiceAlbum] = useRenameServiceAlbumMutation();

  const currentDescription =
    serviceAlbums?.find((album) => album.id === props.serviceAlbumId)?.description ?? '';

  const onSubmit: FormSubmitHandler<FormValues> = (values) =>
    renameServiceAlbum({
      vehicleId: props.vehicleId,
      serviceAlbumId: props.serviceAlbumId,
      renameServiceAlbumRequestBody: {description: values.description},
    })
      .unwrap()
      .then(closeCurrentDialog)
      .then(props.onComplete)
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={20}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.vehicle.albums.dialog.text')}
        </Text>
        <Form<FormValues>
          onSubmit={onSubmit}
          schema={schema}
          defaultValues={{description: currentDescription}}
        >
          {(control) => (
            <VStack spacing={4}>
              <FormField
                control={control}
                name="description"
                type="text"
                label={i18n.t('entity.vehicle.labels.albumName')}
                isRequired
                data-testid={suffixTestId('albumName', props)}
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel', props)}
                />
                <FormButton
                  title={i18n.t('general.actions.save')}
                  type="submit"
                  control={control}
                  data-testid={suffixTestId('submit', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </VStack>
    </DataStatus>
  );
}

const schema = object({
  description: yupString.required().max(255),
});
